import { useMenu } from "@/components/shell/Menu";
import type { MenuItemSpec } from "@/components/shell/menuSpec";
import type { ReadingFolder, ReadingLog } from "@/lib/reading";

import { logLabel } from "./logTabs";

/**
 * A sentence row's `⋯` menu (日本-306), and since 日本-318 its right-click too.
 *
 * Replaced the bare `×` the gutter used to carry. That costs a click on the commonest destructive
 * action — pruning a line where the OCR read the UI chrome instead of the dialogue — and it is what
 * the design draws, because *ログへ移動* has nowhere else to live and a second gutter control
 * would crowd a 42px column that also holds the clock.
 *
 * **Open tabs first in the Move submenu, then `書架から選ぶ…`.** The log you are moving a
 * sentence into is nearly always one you already have open; making that the common case is the
 * difference between a two-click move and a trip through a picker.
 *
 * The menu itself now lives in `components/shell/Menu.tsx` — this file is the `⋯` button and the
 * item list. Splitting the list out is what lets the row's right-click offer exactly the same
 * items without either copy drifting from the other.
 */

export interface SentenceMenuActions {
  logs: ReadingLog[];
  /** So a destination can carry its work as context — see the submenu. */
  folders: ReadingFolder[];
  openLogIds: string[];
  currentLogId: string | null;
  onMove: (logId: string) => void;
  onChooseFromLibrary: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
}

/** The items, so the `⋯` and the row's right-click cannot drift apart. */
export function sentenceMenuItems({
  logs,
  folders,
  openLogIds,
  currentLogId,
  onMove,
  onChooseFromLibrary,
  onEdit,
  onCopy,
  onDelete,
}: SentenceMenuActions): MenuItemSpec[] {
  const destinations = openLogIds.filter((id) => id !== currentLogId);

  return [
    {
      kind: "submenu",
      id: "move",
      label: "ログへ移動",
      // **A `note` row, deliberately not `empty`.** `empty` renders only when the submenu holds
      // nothing selectable, and `書架から選ぶ…` is always there — so the fallback could
      // never fire, and a strip with one tab open showed a bare `書架から選ぶ…` with
      // nothing saying why no destinations were listed. The old menu drew both lines; so does
      // this.
      items: [
        ...(destinations.length === 0
          ? [{ kind: "note" as const, id: "move-none", label: "他に開いているタブはありません" }]
          : []),
        ...destinations.map((logId): MenuItemSpec => {
          const entry = logs.find((candidate) => candidate.id === logId);
          // The folder as dim mono context, because two logs called `04` in different works are
          // otherwise indistinguishable in this list.
          const folder = folders.find((candidate) => candidate.id === entry?.folderId)?.name;
          return {
            kind: "item",
            id: `move-${logId}`,
            label: logLabel(logs, logId),
            ...(folder ? { sub: folder } : {}),
            onSelect: () => onMove(logId),
          };
        }),
        {
          kind: "item",
          id: "move-library",
          label: "書架から選ぶ…",
          onSelect: onChooseFromLibrary,
        },
      ],
    },
    { kind: "item", id: "edit", label: "文を編集", hint: "クリック", onSelect: onEdit },
    { kind: "item", id: "copy", label: "文をコピー", onSelect: onCopy },
    { kind: "separator", id: "sep" },
    { kind: "item", id: "delete", label: "文を削除", danger: true, onSelect: onDelete },
  ];
}

export function SentenceMenu({
  sentenceId,
  isPhone = false,
  ...actions
}: SentenceMenuActions & {
  /** Keys the open state, so only this row's `⋯` renders as pressed. */
  sentenceId: string;
  /** Always visible on a phone, where there is no hover to reveal it. */
  isPhone?: boolean;
}) {
  const menu = useMenu();
  const key = `sentence:${sentenceId}`;
  const open = menu.openKey === key;

  return (
    <button
      type="button"
      onClick={(event) => {
        // The gutter is inside the sentence row, which takes focus on click. Stopping here is the
        // documented rule — a child that handles an event must stop it on every phase it handles.
        event.stopPropagation();
        menu.openFrom(event.currentTarget, sentenceMenuItems(actions), key);
      }}
      aria-label="文の操作"
      aria-expanded={open}
      aria-haspopup="menu"
      title="文の操作"
      className={[
        "rounded-[var(--r-2)] px-[5px] text-[14px]/[1.4] text-ink-4 hover:text-ink-2",
        isPhone ? "" : "opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100",
        open ? "opacity-100" : "",
      ].join(" ")}
    >
      ⋯
    </button>
  );
}
